import { useCallback, useEffect, useState } from "react";
import api, { formatBRL, formatDate, formatDateTime, apiErrorMsg } from "@/lib/api";
import { useRealtime } from "@/contexts/RealtimeContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Ban } from "lucide-react";
import { toast } from "sonner";

const hoje = () => new Date().toLocaleDateString("en-CA");

function RegistrarDialog({ aviso, onClose, onDone }) {
  const [protocolo, setProtocolo] = useState("");
  const [dataCanc, setDataCanc] = useState(hoje());
  const [salvando, setSalvando] = useState(false);
  useEffect(() => { if (aviso) { setProtocolo(""); setDataCanc(hoje()); } }, [aviso]);
  const salvar = async () => {
    if (!protocolo.trim()) return toast.error("Informe o protocolo de cancelamento da NF");
    setSalvando(true);
    try {
      await api.post(`/fiscal/nfs-a-cancelar/${aviso.id}/registrar`, { protocolo: protocolo.trim(), data_cancelamento: dataCanc });
      toast.success(`Cancelamento da NF nº ${aviso.nf_numero} registrado`);
      onDone();
    } catch (e) { toast.error(apiErrorMsg(e, "Erro ao registrar")); } finally { setSalvando(false); }
  };
  return (
    <Dialog open={!!aviso} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="modal-registrar-nf-cancelar">
        <DialogHeader>
          <DialogTitle>Registrar cancelamento da NF nº {aviso?.nf_numero}</DialogTitle>
          <DialogDescription>Cancele a nota no emissor da prefeitura e informe aqui o protocolo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Protocolo de cancelamento *</Label>
            <Input value={protocolo} onChange={(e) => setProtocolo(e.target.value)} placeholder="Nº do protocolo" data-testid="nfc-protocolo" /></div>
          <div><Label>Data do cancelamento</Label>
            <Input type="date" value={dataCanc} onChange={(e) => setDataCanc(e.target.value)} data-testid="nfc-data" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="nfc-voltar">Voltar</Button>
          <Button onClick={salvar} disabled={salvando} className="bg-red-600 hover:bg-red-700" data-testid="nfc-salvar">Registrar cancelamento</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AvisoLinha({ a, onRegistrar }) {
  return (
    <div className="flex items-center gap-3 flex-wrap bg-white border border-red-200 rounded-lg p-3" data-testid={`nfc-item-${a.id}`}>
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-bold text-red-900">Cancelar NF nº {a.nf_numero} · {a.municipio_nome}<span className="text-xs text-slate-500"> / {a.municipio_uf}</span></div>
        <div className="text-xs text-slate-600">
          {a.curso_nome}{a.curso_uf ? `. ${a.curso_uf}` : ""} · <b className="tabular-nums">{formatBRL(a.valor || 0)}</b>
          {a.nf_data_emissao ? ` · emitida em ${formatDate(a.nf_data_emissao)}` : ""}
        </div>
        <div className="text-xs text-red-700 mt-0.5" data-testid={`nfc-motivo-${a.id}`}>
          Inscrição cancelada por {a.inscricao_cancelada_por_nome} em {formatDateTime(a.inscricao_cancelada_em)}{a.motivo ? ` · ${a.motivo}` : ""}
        </div>
      </div>
      <Button size="sm" onClick={() => onRegistrar(a)} className="bg-red-600 hover:bg-red-700 text-xs" data-testid={`nfc-registrar-${a.id}`}>
        <Ban className="w-3 h-3 mr-1" /> Registrar cancelamento
      </Button>
    </div>
  );
}

export function NfsACancelar() {
  const { lastEvent } = useRealtime() || {};
  const [itens, setItens] = useState([]);
  const [aviso, setAviso] = useState(null);
  const load = useCallback(() => api.get("/fiscal/nfs-a-cancelar").then((r) => setItens(r.data || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (["nf_a_cancelar", "nf_cancelada"].includes(lastEvent?.event)) load(); }, [lastEvent, load]);
  if (!itens.length) return null;
  return (
    <Card className="p-4 rounded-2xl border-red-300 bg-red-50 space-y-2" data-testid="nfs-a-cancelar">
      <div className="flex items-center gap-2">
        <Ban className="w-5 h-5 text-red-600" />
        <h2 className="font-display font-bold text-lg text-red-900">NFs para cancelar · inscrição cancelada</h2>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-red-600 text-white" data-testid="nfs-a-cancelar-qtd">{itens.length}</span>
      </div>
      {itens.map((a) => <AvisoLinha key={a.id} a={a} onRegistrar={setAviso} />)}
      <RegistrarDialog aviso={aviso} onClose={() => setAviso(null)} onDone={() => { setAviso(null); load(); }} />
    </Card>
  );
}
